"use client";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const formatChartInt = (value: number) => Math.round(Number(value || 0)).toLocaleString("en-IN");

export type PayrollThresholdCluster = {
  cluster: string;
  a: number;
  b: number;
};

export function buildPfThresholdChartData(pf: Record<string, unknown>): PayrollThresholdCluster[] | null {
  const recorded = Number(pf.Recorded_Amount);
  const expected = Number(pf.Expected_PF);
  const threshold = Number(pf.Threshold);
  const difference = Number(pf.Difference);
  if ([recorded, expected, threshold, difference].some((n) => Number.isNaN(n))) {
    return null;
  }
  return [
    { cluster: "Recorded vs expected PF", a: recorded, b: expected },
    { cluster: "Threshold vs |difference|", a: threshold, b: Math.abs(difference) },
  ];
}

export function buildSalaryThresholdChartData(calc: Record<string, unknown>): PayrollThresholdCluster[] | null {
  const actual = Number(calc.CY_Net_Salary);
  const expected = Number(calc.Salary_Expectation);
  const threshold = Number(calc.Threshold ?? calc.Final_threshold);
  const difference = Number(calc.Difference);
  if ([actual, expected, threshold, difference].some((n) => Number.isNaN(n))) {
    return null;
  }
  return [
    { cluster: "CY net vs salary expectation", a: actual, b: expected },
    { cluster: "Threshold vs |difference|", a: threshold, b: Math.abs(difference) },
  ];
}

export function buildPfThresholdChartFromCalculation(p: {
  recordedAmount: number;
  expectedPF: number;
  threshold: number;
  difference: number;
}): PayrollThresholdCluster[] {
  return [
    { cluster: "Recorded vs expected PF", a: p.recordedAmount, b: p.expectedPF },
    { cluster: "Threshold vs |difference|", a: p.threshold, b: p.difference },
  ];
}

export function extractPlainInferenceText(textResponse: unknown): string {
  if (textResponse == null) return "";
  if (typeof textResponse === "string") return textResponse;
  if (typeof textResponse === "object") {
    const o = textResponse as Record<string, unknown>;
    if (typeof o.plainText === "string") return o.plainText;
    if (typeof o.html === "string") return String(o.html).replace(/<[^>]*>/g, "");
  }
  return "";
}

export function payrollInferenceTextPayload(plain: string) {
  const t = plain.trim();
  if (!t) return null;
  return {
    html: t.split("\n").join("<br/>"),
    plainText: t,
  };
}

export function PayrollAnalyticalThresholdChart({ data }: { data: PayrollThresholdCluster[] }) {
  if (!data?.length) return null;
  return (
    <div className="h-72 w-full min-h-[18rem] rounded-lg border border-white/10 bg-white/[0.03] p-3">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey="cluster"
            tick={{ fill: "#cbd5e1", fontSize: 10 }}
            interval={0}
            angle={-10}
            textAnchor="end"
            height={56}
          />
          <YAxis tick={{ fill: "#cbd5e1", fontSize: 11 }} tickFormatter={(v) => formatChartInt(Number(v))} />
          <Tooltip formatter={(value: number) => formatChartInt(Number(value))} />
          <Legend />
          <Bar dataKey="a" fill="#38bdf8" name="Metric A" />
          <Bar dataKey="b" fill="#a78bfa" name="Metric B" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
